import { useCallback } from "react";
import axios from "axios";
import Config from "../../Constants/Config";
import { useAuthDetails } from "../../Common/cookiesHelper";

/**
 * Authenticated POST to the QuickBooks endpoints (quickvee-backend-node,
 * router/merchant/quickbooks_router.js).
 *
 * Every endpoint runs behind authenticateRequest, which needs the Bearer token
 * in the header AND merchant_id / token_id / login_type in the JSON body.
 *
 * As in HqConnect: useAuthDetails() builds a fresh object each render, so the
 * callbacks depend on the primitive fields, never the object — otherwise every
 * effect that depends on `post` would re-fire on every render.
 */
export function useQboApi() {
  const { userTypeData, LoginGetDashBoardRecordJson } = useAuthDetails();
  const merchantId = LoginGetDashBoardRecordJson?.data?.merchant_id;
  const authToken = userTypeData?.token;
  const authTokenId = userTypeData?.token_id;
  const authLoginType = userTypeData?.login_type;

  const post = useCallback(
    async (endpoint, payload = {}, headers = {}) => {
      const { data } = await axios.post(
        Config.BASE_NODE_URL + endpoint,
        { merchant_id: merchantId, token_id: authTokenId, login_type: authLoginType, ...payload },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            ...headers,
          },
        }
      );
      return data;
    },
    [merchantId, authToken, authTokenId, authLoginType]
  );

  return {
    post,
    ready: Boolean(merchantId && authToken),
    merchantId,
    loginType: authLoginType,
  };
}

/** Server messages are already written for merchants; fall back only if absent. */
export const readError = (err, fallback = "Something went wrong. Please try again.") =>
  err?.response?.data?.message || err?.response?.data?.error?.message || fallback;

export const readErrorCode = (err) => err?.response?.data?.code || null;
